import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllTemplatesThunk,
  createTemplateThunk,
  updateTemplateByIdThunk,
  deleteTemplateByIdThunk,
} from "../../../../store/thunks/jewelleryPlan/planTemplateThunks";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline";

const TemplateManager = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { templates, loading } = useSelector(
    (state) => state.jewelleryPlan?.planTemplates
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [filter, setFilter] = useState("active");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    await dispatch(getAllTemplatesThunk());
  };

  const handleCreateTemplate = async (templateData) => {
    try {
      await dispatch(createTemplateThunk(templateData)).unwrap();
      setShowCreateModal(false);
      fetchTemplates();
    } catch (error) {
      console.error("Failed to create template:", error);
      alert(`Error creating template: ${error}`);
    }
  };

  const handleUpdateTemplate = async (id, updates) => {
    try {
      // Fixed: Pass templateId and templateData as expected by the thunk
      await dispatch(
        updateTemplateByIdThunk({
          templateId: id,
          templateData: updates,
        })
      ).unwrap();
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error("Failed to update template:", error);
      alert(`Error updating template: ${error}`);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await dispatch(deleteTemplateByIdThunk(id)).unwrap();
        fetchTemplates();
      } catch (error) {
        console.error("Failed to delete template:", error);
        alert(`Error deleting template: ${error}`);
      }
    }
  };

  const handleDuplicateTemplate = (template) => {
    const duplicatedTemplate = {
      ...template,
      name: `${template.name} (Copy)`,
      code: `${template.code}_COPY_${Date.now()}`, // Generate unique code
      isActive: false,
    };
    delete duplicatedTemplate._id;
    setEditingTemplate(duplicatedTemplate);
    setShowCreateModal(true);
  };

  const filteredTemplates = templates?.filter((template) => {
    if (filter === "active") return template.isActive;
    if (filter === "inactive") return !template.isActive;
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">Plan Templates</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              Manage jewellery savings plan templates
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-primary text-text-on-primary rounded-md hover:bg-primary-dark"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Template
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background shadow rounded-lg p-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-md ${
              filter === "all"
                ? "bg-primary text-text-on-primary"
                : "bg-neutral-light text-text hover:bg-neutral-dark hover:text-text-on-primary"
            }`}
          >
            All Templates
          </button>
          <button
            onClick={() => setFilter("active")}
            className={`px-4 py-2 rounded-md ${
              filter === "active"
                ? "bg-success text-text-on-primary"
                : "bg-neutral-light text-text hover:bg-success-light"
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setFilter("inactive")}
            className={`px-4 py-2 rounded-md ${
              filter === "inactive"
                ? "bg-neutral-dark text-text-on-primary"
                : "bg-neutral-light text-text hover:bg-neutral-dark hover:text-text-on-primary"
            }`}
          >
            Inactive
          </button>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : filteredTemplates?.length > 0 ? (
          filteredTemplates.map((template) => (
            <div
              key={template._id}
              className="bg-background rounded-lg shadow hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-text">
                      {template.name}
                    </h3>
                    <p className="text-xs text-neutral-dark mt-1">
                      Code: {template.code}
                    </p>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-2 ${
                        template.isActive
                          ? "bg-success-light text-success-dark"
                          : "bg-neutral-light text-neutral-dark"
                      }`}
                    >
                      {template.isActive ? (
                        <>
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="h-3 w-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* Template Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-dark">Type:</span>
                    <span className="font-medium">{template.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-dark">Duration:</span>
                    <span className="font-medium">
                      {template.tenureMonths} months
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-dark">Min Installment:</span>
                    <span className="font-medium">
                      ₹{template.minInstallment?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-dark">Max Installment:</span>
                    <span className="font-medium">
                      ₹{template.maxInstallment?.toLocaleString()}
                    </span>
                  </div>
                  {template.benefitType && (
                    <div className="flex justify-between">
                      <span className="text-neutral-dark">Benefit:</span>
                      <span className="font-medium">
                        {template.benefitType}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-light">
                  <button
                    onClick={() => setEditingTemplate(template)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-1.5 border border-neutral-light rounded-md hover:bg-neutral-light"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDuplicateTemplate(template)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-1.5 border border-neutral-light rounded-md hover:bg-neutral-light"
                  >
                    <DuplicateIcon className="h-4 w-4 mr-1" />
                    Clone
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template._id)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-1.5 border border-danger-light rounded-md text-danger hover:bg-danger-light"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-background shadow rounded-lg p-12 text-center">
            <p className="text-neutral-dark">No templates found</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTemplate) && (
        <TemplateModal
          template={editingTemplate}
          onSave={
            editingTemplate && editingTemplate._id
              ? (data) => handleUpdateTemplate(editingTemplate._id, data)
              : handleCreateTemplate
          }
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
};

// Template Modal Component
const TemplateModal = ({ template, onSave, onClose }) => {
  // Generate a unique code for new templates
  const generateCode = (name) => {
    const cleanName = name.toUpperCase().replace(/[^A-Z0-9]/g, "_");
    const timestamp = Date.now().toString().slice(-4);
    return `${cleanName}_${timestamp}`;
  };

  const [formData, setFormData] = useState({
    // Required fields based on backend validation
    name: template?.name || "",
    code: template?.code || "",
    type: template?.type || "VALUE_SAVINGS",
    tenureMonths: template?.tenureMonths || 11,
    minInstallment: template?.minInstallment || 1000,
    maxInstallment: template?.maxInstallment || 100000,
    terms:
      template?.terms ||
      "Standard terms and conditions apply. Please refer to the detailed terms document.",

    // Optional fields
    benefitType: template?.benefitType || "MAKING_CHARGE_WAIVER",
    benefitParams: template?.benefitParams || {},
    policies: template?.policies || {
      allowPartialPayment: false,
      allowAdvancePayment: true,
      allowPartialRedemption: false,
      minRedemptionMonth: 8,
      penaltyOnDefault: 0,
      gracePeriodDays: 7,
    },
    isActive: template?.isActive !== undefined ? template.isActive : true,
  });

  // Auto-generate code when name changes (for new templates only)
  useEffect(() => {
    if (!template && formData.name) {
      setFormData((prev) => ({
        ...prev,
        code: generateCode(formData.name),
      }));
    }
  }, [formData.name, template]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (formData.minInstallment >= formData.maxInstallment) {
      alert("Maximum installment must be greater than minimum installment");
      return;
    }

    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {template && template._id ? "Edit Template" : "Create Template"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Information */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-medium mb-3">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
                  required
                  placeholder="e.g., Gold Savings Plan"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Template Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      code: e.target.value.toUpperCase(),
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
                  required
                  placeholder="e.g., GOLD_SAVE_001"
                  pattern="[A-Z0-9_]+"
                  title="Only uppercase letters, numbers and underscores allowed"
                />
                <p className="text-xs text-neutral-dark mt-1">
                  Unique identifier for this template
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Plan Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="VALUE_SAVINGS">Value Savings</option>
                  <option value="WEIGHT_SAVINGS">Weight Savings</option>
                  <option value="HYBRID">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Tenure (months) *
                </label>
                <input
                  type="number"
                  value={formData.tenureMonths}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tenureMonths: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
                  min="1"
                  max="60"
                  required
                />
              </div>
            </div>
          </div>

          {/* Installment Configuration */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-medium mb-3">
              Installment Configuration
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Min Installment (₹) *
                </label>
                <input
                  type="number"
                  value={formData.minInstallment}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minInstallment: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
                  min="100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Max Installment (₹) *
                </label>
                <input
                  type="number"
                  value={formData.maxInstallment}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxInstallment: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
                  min="100"
                  required
                />
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-medium mb-3">Benefits</h3>
            <div>
              <label className="block text-sm font-medium text-text mb-1">
                Benefit Type
              </label>
              <select
                value={formData.benefitType}
                onChange={(e) =>
                  setFormData({ ...formData, benefitType: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
              >
                <option value="MAKING_CHARGE_WAIVER">
                  Making Charge Waiver
                </option>
                <option value="VA_DISCOUNT_CAP">VA Discount Cap</option>
                <option value="FREE_INSTALLMENT">Free Installment</option>
                <option value="BONUS_GRAMS">Bonus Grams</option>
                <option value="NONE">None</option>
              </select>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-medium mb-3">Terms and Conditions</h3>
            <div>
              <label className="block text-sm font-medium text-text mb-1">
                Terms *
              </label>
              <textarea
                value={formData.terms}
                onChange={(e) =>
                  setFormData({ ...formData, terms: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
                rows="4"
                required
                placeholder="Enter the terms and conditions for this plan template..."
              />
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="rounded border-neutral-light text-primary focus:ring-primary"
            />
            <label className="ml-2 text-sm text-text">
              Active Template (Available for new enrollments)
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-light rounded-md hover:bg-neutral-light"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-text-on-primary rounded-md hover:bg-primary-dark"
            >
              {template && template._id ? "Update" : "Create"} Template
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateManager;
